import { t } from "@lingui/macro";
import { useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Agent } from "../types";
import { isAdminRole } from "../utils/auth";
import { SettingsGroup } from "./SettingsGroup";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

type Tools = NonNullable<Agent["dtmfCollectionTools"]>;
const keys = [
	"askForPhoneNumber",
	"askForLicenseNumber",
	"askForIdNumber",
] as const;

function AgentKeypadSettings({
	agent,
	save,
}: {
	agent: Agent;
	save: (agent: Agent) => Promise<void>;
}) {
	const [draft, setDraft] = useState<{ agent: Agent; tools: Tools } | null>(
		null,
	);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(false);
	const tools = draft?.tools ?? agent.dtmfCollectionTools ?? {};
	const labels = {
		askForPhoneNumber: t({
			id: "agentEditor.dtmf.phoneNumber",
			message: "Phone number",
		}),
		askForLicenseNumber: t({
			id: "agentEditor.dtmf.licenseNumber",
			message: "License number",
		}),
		askForIdNumber: t({
			id: "agentEditor.dtmf.idNumber",
			message: "ID number",
		}),
	};
	const dirty =
		draft !== null &&
		keys.some(
			(key) => !!tools[key] !== !!draft.agent.dtmfCollectionTools?.[key],
		);
	const handleSave = async () => {
		if (!draft || !dirty) return;
		setSaving(true);
		setError(false);
		try {
			// Keep the editing snapshot's updatedAt for the existing conflict check.
			await save({ ...draft.agent, dtmfCollectionTools: draft.tools });
			setDraft(null);
			toast.success(
				t({
					id: "settings.dtmfSaved",
					message: "Keypad settings saved. Redeploy the agent to apply them.",
				}),
			);
		} catch {
			setError(true);
		} finally {
			setSaving(false);
		}
	};
	return (
		<fieldset
			className="min-w-0 space-y-3 py-4 first:pt-0 last:pb-0"
			disabled={saving}
		>
			<legend className="float-start mb-3 w-full break-words font-semibold">
				{agent.name}
			</legend>
			<div className="clear-both flex flex-wrap items-center gap-x-6 gap-y-3">
				{keys.map((key) => (
					<label key={key} className="flex items-center gap-2">
						<Switch
							checked={tools[key] === true}
							onCheckedChange={(enabled) => {
								setError(false);
								setDraft((current) => ({
									agent: current?.agent ?? agent,
									tools: {
										...(current?.tools ?? agent.dtmfCollectionTools),
										[key]: enabled,
									},
								}));
							}}
						/>
						<span className="text-sm">{labels[key]}</span>
					</label>
				))}
				<div className="flex gap-2 sm:ms-auto">
					<Button
						size="sm"
						onClick={() => void handleSave()}
						disabled={!dirty || saving}
					>
						{saving
							? t({ id: "saving", message: "Saving..." })
							: t({ id: "saveChanges", message: "Save Changes" })}
					</Button>
					{draft && (
						<Button
							size="sm"
							variant="ghost"
							disabled={saving}
							onClick={() => {
								setDraft(null);
								setError(false);
							}}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
					)}
				</div>
			</div>
			{error && (
				<p role="alert" className="text-sm text-destructive">
					{t({
						id: "settings.dtmfSaveFailed",
						message:
							"Could not save keypad settings. Retry, or cancel to load the latest agent settings.",
					})}
				</p>
			)}
		</fieldset>
	);
}

export function DtmfSettingsCard() {
	const { agents, user, updateAgent } = useData();
	if (!isAdminRole(user?.role)) return null;
	return (
		<SettingsGroup
			header={
				<h3>
					{t({ id: "settings.dtmfTitle", message: "Keypad input (DTMF)" })}
				</h3>
			}
			description={t({
				id: "agentEditor.help.dtmfCollectionTools",
				message:
					"Let the agent collect confirmed numeric identifiers by keypad.",
			})}
		>
			<p className="text-sm text-muted-foreground">
				{t({
					id: "settings.dtmfDeployment",
					message:
						"Save changes for each agent, then redeploy it from the Agents page. These settings do not change running agents immediately.",
				})}
			</p>
			{agents.length ? (
				<div className="divide-y divide-card-border">
					{agents.map((agent) => (
						<AgentKeypadSettings
							key={agent.id}
							agent={agent}
							save={updateAgent}
						/>
					))}
				</div>
			) : (
				<p className="text-sm text-muted-foreground">
					{t({
						id: "settings.dtmfNoAgents",
						message: "Create an agent before configuring keypad input.",
					})}
				</p>
			)}
		</SettingsGroup>
	);
}
